import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { appendPath, getValue } from '@truqc/content-path';
import entityComposition from '../../../common/classes/entityComposition';
const styles = {
  markerContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  mapboxglMarker: {
    backgroundImage: 'url(../../../../../img/map/map-marker-red.png)',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    backgroundSize: 'contain',
    width: '30px',
    height: '30px',
    cursor: 'pointer'
  }
};
const mapTypes = {
  standard: 'mapbox://styles/mapbox/streets-v11',
  satellite: 'mapbox://styles/mapbox/satellite-v9',
  hybrid: 'mapbox://styles/mapbox/satellite-streets-v11'
};
const MapView = ({
  contentPath,
  form: _form = {
    active: {
      content: {}
    }
  },
  issue: _issue = {
    active: {
      content: {}
    }
  },
  login: {
    user
  },
  report: _report = {
    active: {
      content: {}
    }
  },
  parentContentPath,
  rotateEnabled: _rotateEnabled = false,
  scrollEnabled: _scrollEnabled = true,
  showsCompass: _showsCompass = false,
  showsScale: _showsScale = false,
  showsUserLocation: _showsUserLocation = false,
  zoomEnabled: _zoomEnabled = true
}) => {
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [mapType, setMapType] = useState(null);
  const [sourceType, setSourceType] = useState(null);
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const tokenRef = useRef();
  const source = useMemo(() => {
    const candidate = [_form, _report, _issue].find(obj => {
      var _obj$active;
      return obj === null || obj === void 0 || (_obj$active = obj.active) === null || _obj$active === void 0 ? void 0 : _obj$active.content;
    });
    if (candidate) {
      return candidate.active;
    }
    return null;
  }, [_form, _report, _issue]);
  useEffect(() => {
    if (source) {
      setSourceType(source.entity);
    }
  }, [source]);
  const mapboxToken = user === null || user === void 0 ? void 0 : user.mapboxToken;
  if (mapboxToken) {
    tokenRef.current = mapboxToken;
  }
  const removeMarkers = () => {
    const map = mapInstanceRef.current;
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];
    if (!map) return;
    map.flyTo({
      center: [0, 0],
      zoom: 0,
      speed: 2
    });
  };
  const updateMarker = useCallback((lng, lat) => {
    const map = mapInstanceRef.current;
    if (!map || lng == null || lat == null) return;
    const markerContainer = document.createElement('div');
    Object.assign(markerContainer.style, styles.markerContainer);
    const markerElement = document.createElement('div');
    Object.assign(markerElement.style, styles.mapboxglMarker);
    markerContainer.appendChild(markerElement);
    const marker = new mapboxgl.Marker({
      element: markerContainer
    }).setLngLat([lng, lat]).addTo(map);
    map.flyTo({
      center: [lng, lat],
      zoom: 12,
      speed: 2
    });
    markersRef.current.push(marker);
  }, []);
  const updateMapStyle = useCallback((type, lng, lat) => {
    const map = mapInstanceRef.current;
    if (!map || !type) return;
    map.setStyle(type);
    map.once('styledata', () => {
      if (lng != null && lat != null) {
        updateMarker(lng, lat);
      }
    });
  }, [updateMarker]);
  useEffect(() => {
    var _markerLocation$latit, _markerLocation$longi, _mapTypes$typeKey;
    const {
      markerLocation = {},
      mapType: newMapType = {}
    } = getValue(appendPath(parentContentPath, contentPath), source.content) || {};
    let typeKey = Object.keys(newMapType)[0] || 'standard';
    if (sourceType === 'issue') {
      const {
        mapType: issueMapType = {}
      } = getValue(parentContentPath, source.content) || {};
      if (issueMapType !== null && issueMapType !== void 0 && issueMapType.identifier) {
        typeKey = issueMapType.identifier;
      }
    }
    setLatitude((_markerLocation$latit = markerLocation.latitude) !== null && _markerLocation$latit !== void 0 ? _markerLocation$latit : null);
    setLongitude((_markerLocation$longi = markerLocation.longitude) !== null && _markerLocation$longi !== void 0 ? _markerLocation$longi : null);
    setMapType((_mapTypes$typeKey = mapTypes[typeKey]) !== null && _mapTypes$typeKey !== void 0 ? _mapTypes$typeKey : mapTypes.standard);
  }, [source, sourceType, contentPath, parentContentPath]);
  useEffect(() => {
    if (!mapContainerRef.current || !tokenRef.current) return;
    mapboxgl.accessToken = tokenRef.current;
    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: mapType || mapTypes.standard,
      center: longitude && latitude ? [longitude, latitude] : [0, 0],
      zoom: longitude && latitude ? 12 : 0,
      scrollZoom: _zoomEnabled,
      dragPan: _scrollEnabled
    });
    if (!_rotateEnabled) {
      map.dragRotate.disable();
      map.touchZoomRotate.disableRotation();
    }
    if (_showsUserLocation && _scrollEnabled) {
      // Add the GeolocateControl to show the user's location
      const geolocateControl = new mapboxgl.GeolocateControl({
        positionOptions: {
          enableHighAccuracy: true
        },
        showUserHeading: true
      });
      map.addControl(geolocateControl, 'top-right');
    }
    if (!_zoomEnabled) {
      map.scrollZoom.disable();
      map.doubleClickZoom.disable();
    }
    if (_showsCompass || _zoomEnabled) {
      const navControl = new mapboxgl.NavigationControl({
        showCompass: _showsCompass,
        showZoom: _zoomEnabled
      });
      map.addControl(navControl, 'top-right');
    }
    if (_showsScale) {
      const scaleControl = new mapboxgl.ScaleControl({
        maxWidth: 200,
        unit: 'imperial'
      });
      map.addControl(scaleControl, 'bottom-left');
    }
    mapInstanceRef.current = map;

    // eslint-disable-next-line consistent-return
    return () => {
      map.remove();
      removeMarkers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !mapType) return;
    updateMapStyle(mapType, longitude, latitude);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapType, updateMapStyle]);
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;
    if (longitude == null || latitude == null) {
      removeMarkers();
      return;
    }
    updateMarker(longitude, latitude);
  }, [longitude, latitude, updateMarker]);
  return tokenRef.current ? /*#__PURE__*/React.createElement("div", {
    "data-testid": "map-container",
    style: {
      width: '100%',
      height: '500px',
      position: 'relative'
    },
    ref: mapContainerRef
  }) : null;
};
MapView.displayName = 'Map';
MapView.propTypes = {
  contentPath: PropTypes.string.isRequired,
  form: PropTypes.shape({
    formTypeIdentifier: PropTypes.string,
    active: PropTypes.shape({
      content: PropTypes.shape({})
    })
  }),
  issue: PropTypes.shape({
    active: PropTypes.shape({
      content: PropTypes.shape({})
    })
  }),
  login: PropTypes.shape({
    user: PropTypes.shape({
      mapboxToken: PropTypes.string
    })
  }).isRequired,
  parentContentPath: PropTypes.string.isRequired,
  report: PropTypes.shape({
    active: PropTypes.shape({
      content: PropTypes.shape({})
    })
  }),
  rotateEnabled: PropTypes.bool,
  scrollEnabled: PropTypes.bool,
  showsCompass: PropTypes.bool,
  showsScale: PropTypes.bool,
  showsUserLocation: PropTypes.bool,
  zoomEnabled: PropTypes.bool
};
export { MapView as DisconnectedMapView };
export default entityComposition(MapView, ['form', 'report', 'issue', 'login'], {}, []);